import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { webConfig } from "../../config";

const API_URL = webConfig.apiUrl;

const STORAGE_KEYS = {
  LANGUAGE: "language",
  TRANSLATIONS: "translations",
  VERSION: "translation_version",
};

const getLanguageFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("lang");
};

const getLanguageFromStorage = () => {
  return localStorage.getItem(STORAGE_KEYS.LANGUAGE);
};

const getBrowserLanguage = () => {
  return navigator.language.split("-")[0];
};

const detectLanguage = () => {
  const lang =
    getLanguageFromURL() ||
    getLanguageFromStorage() ||
    getBrowserLanguage() ||
    "en";

  return lang;
};

function getCachedTranslations(language) {
  try {
    const cached = localStorage.getItem(
      `${STORAGE_KEYS.TRANSLATIONS}_${language}`
    );
    const version = localStorage.getItem(`${STORAGE_KEYS.VERSION}_${language}`);

    if (cached && version) {
      return {
        translations: JSON.parse(cached),
        version: parseInt(version),
      };
    }
  } catch (error) {
    console.error("Error reading cached translations:", error);
  }
  return null;
}

function cacheTranslations(language, translations, version) {
  try {
    localStorage.setItem(
      `${STORAGE_KEYS.TRANSLATIONS}_${language}`,
      JSON.stringify(translations)
    );
    localStorage.setItem(
      `${STORAGE_KEYS.VERSION}_${language}`,
      version.toString()
    );
  } catch (error) {
    console.error("Error caching translations:", error);
  }
}

async function checkForUpdates(language, currentVersion) {
  try {
    const response = await fetch(`${API_URL}/translations/ui/version`);
    if (!response.ok) return;

    const { version: latestVersion } = await response.json();

    if (currentVersion < latestVersion) {
      const translationResponse = await fetch(
        `${API_URL}/translations/ui/${language}`
      );

      if (translationResponse.ok) {
        const data = await translationResponse.json();
        cacheTranslations(language, data.translations, data.versionNumber);

        if (i18n.isInitialized) {
          i18n.addResourceBundle(
            language,
            "translation",
            data.translations,
            true,
            true
          );
        }
      }
    }
  } catch (error) {
    console.error("Error checking translation version:", error);
  }
}

export async function loadTranslations(language) {
  try {
    const cachedData = getCachedTranslations(language);
    if (cachedData) {
      i18n.addResourceBundle(
        language,
        "translation",
        cachedData.translations,
        true,
        true
      );
      checkForUpdates(language, cachedData.version);
      return cachedData.translations;
    }

    const response = await fetch(`${API_URL}/translations/ui/${language}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch translations for ${language}`);
    }

    const data = await response.json();

    cacheTranslations(language, data.translations, data.versionNumber);

    i18n.addResourceBundle(
      language,
      "translation",
      data.translations,
      true,
      true
    );

    return data.translations;
  } catch (error) {
    console.error("Error loading translations:", error);

    if (language !== "en") {
      return loadTranslations("en");
    }
    return {};
  }
}

const initialLanguage = detectLanguage();

i18n.use(initReactI18next).init({
  lng: initialLanguage,
  fallbackLng: "en",
  debug: false,
  resources: {},
  interpolation: {
    escapeValue: false,
  },
  returnEmptyString: false,
  react: {
    useSuspense: false,
  },
});

const cachedData = getCachedTranslations(initialLanguage);
if (cachedData) {
  i18n.addResourceBundle(
    initialLanguage,
    "translation",
    cachedData.translations,
    true,
    true
  );
  checkForUpdates(initialLanguage, cachedData.version);
} else {
  loadTranslations(initialLanguage);
}

// Always load English as fallback
if (initialLanguage !== "en") {
  const cachedEnglish = getCachedTranslations("en");
  if (cachedEnglish) {
    i18n.addResourceBundle(
      "en",
      "translation",
      cachedEnglish.translations,
      true,
      true
    );
    checkForUpdates("en", cachedEnglish.version);
  } else {
    loadTranslations("en");
  }
}

export default i18n;
export { STORAGE_KEYS };
